import { Filter, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PeriodSelectControl } from '@/components/common/PeriodSelectControl';

export const SalesFiltersDeck = ({
  salesCount,
  totalSoldLabel,
  activeLocationName,
  activeFilterChips = [],
  period,
  onPeriodChange,
  periodOptions,
  customStartDate,
  customEndDate,
  onCustomStartChange,
  onCustomEndChange,
  locationValue = 'all',
  onLocationChange,
  locations = [],
  employeeValue = 'all',
  onEmployeeChange,
  employees = [],
  statusValue = 'all',
  onStatusChange,
  statusOptions = [],
  statusLabels = {},
  paymentAccountValue = 'all',
  onPaymentAccountChange,
  paymentAccounts = [],
  onResetFilters,
  actionsSlot = null,
}) => (
  <Card className="border-slate-200 shadow-sm" data-testid="sales-filters-deck">
    <CardContent className="space-y-3 p-3">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Filter className="h-4 w-4 text-slate-500" />
          Filtros de vendas
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="sales-active-filters-strip">
          <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700" data-testid="sales-active-location-badge">
            POS: {activeLocationName || 'Sem localização'}
          </Badge>
          <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700" data-testid="sales-active-count-badge">
            {salesCount} vendas
          </Badge>
          <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700" data-testid="sales-active-total-badge">
            {totalSoldLabel}
          </Badge>
          {activeFilterChips.length > 0 ? activeFilterChips.map((chip) => (
            <Badge key={chip.key} variant="outline" className="rounded-full px-3 py-1 text-slate-600" data-testid={`sales-active-filter-chip-${chip.key}`}>
              {chip.label}
            </Badge>
          )) : (
            <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-600" data-testid="sales-active-filter-chip-default">
              Todo período
            </Badge>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="sales-primary-filters-panel">
        <div className="hidden xl:flex xl:flex-nowrap xl:items-end xl:gap-1.5" data-testid="sales-filters-inline-layout">
          <div className="min-w-0 flex-[1.16]" data-testid="sales-filter-block-period">
            <Label className="text-[11px] text-muted-foreground">Período</Label>
            <div className="mt-1 flex items-center gap-1.5">
              <PeriodSelectControl
                period={period}
                onPeriodChange={onPeriodChange}
                periodOptions={periodOptions}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onCustomStartChange={onCustomStartChange}
                onCustomEndChange={onCustomEndChange}
                customRangeMode="popover"
                customTriggerLabel="Datas"
                selectTriggerClassName="h-8 min-w-[132px] rounded-lg border-slate-200 bg-white text-[11px]"
                customTriggerClassName="h-8 rounded-lg border-slate-200 bg-white px-2 text-[10px]"
                inputClassName="h-8 w-[132px] rounded-lg border-slate-200 bg-white text-[11px]"
                selectTestId="sales-period-selector"
                customRangeTestId="sales-custom-period-range"
                customStartTestId="sales-custom-start-date-input"
                customEndTestId="sales-custom-end-date-input"
                customTriggerTestId="sales-custom-period-trigger"
              />
            </div>
          </div>

          <div className="min-w-0 flex-[0.86]" data-testid="sales-filter-block-location">
            <Label className="text-[11px] text-muted-foreground">Localização</Label>
            <Select value={locationValue} onValueChange={(value) => onLocationChange(value === 'all' ? null : value)}>
              <SelectTrigger className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="sales-location-filter-select">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-0 flex-[0.78]" data-testid="sales-filter-block-employee">
            <Label className="text-[11px] text-muted-foreground">Colaborador</Label>
            <Select value={employeeValue} onValueChange={(value) => onEmployeeChange(value === 'all' ? null : value)}>
              <SelectTrigger className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="sales-employee-filter-select">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {employees.map((employee) => <SelectItem key={employee.id} value={employee.id}>{employee.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-0 flex-[0.68]" data-testid="sales-filter-block-status">
            <Label className="text-[11px] text-muted-foreground">Estado</Label>
            <Select value={statusValue} onValueChange={(value) => onStatusChange(value === 'all' ? null : value)}>
              <SelectTrigger className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="sales-status-filter-select">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {statusOptions.map((status) => <SelectItem key={status} value={status}>{statusLabels[status] || status}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-0 flex-[0.86]" data-testid="sales-filter-block-payment-account">
            <Label className="text-[11px] text-muted-foreground">Conta / pagamento</Label>
            <Select value={paymentAccountValue} onValueChange={(value) => onPaymentAccountChange(value === 'all' ? null : value)}>
              <SelectTrigger className="mt-1 h-8 rounded-lg border-slate-200 bg-white text-[11px]" data-testid="sales-payment-account-filter-select">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {paymentAccounts.map((account) => <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[132px] flex-[0.44]" data-testid="sales-filter-block-actions">
            <Label className="sr-only">Acções</Label>
            <div className="mt-1 flex items-center justify-end gap-1">
              {actionsSlot}
              <Button type="button" variant="outline" className="h-8 w-8 rounded-lg border-slate-200 bg-white px-0 text-[10px]" onClick={onResetFilters} data-testid="sales-reset-filters-button" title="Limpar filtros">
                <RotateCcw className="h-4 w-4" />
                <span className="sr-only">Limpar filtros</span>
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-3 xl:hidden" data-testid="sales-mobile-filters-layout">
          <div>
            <Label className="text-[11px] text-muted-foreground">Período</Label>
            <div className="mt-1.5 grid gap-2">
              <PeriodSelectControl
                period={period}
                onPeriodChange={onPeriodChange}
                periodOptions={periodOptions}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                onCustomStartChange={onCustomStartChange}
                onCustomEndChange={onCustomEndChange}
                selectTriggerClassName="h-9 rounded-lg border-slate-200 bg-white text-sm"
                customRangeClassName="grid grid-cols-2 gap-2 rounded-none border-0 bg-transparent px-0 py-0"
                inputClassName="h-9 w-full rounded-lg border-slate-200 bg-white text-sm"
                selectTestId="sales-period-selector-mobile"
                customRangeTestId="sales-custom-period-range-mobile"
                customStartTestId="sales-custom-start-date-input-mobile"
                customEndTestId="sales-custom-end-date-input-mobile"
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Localização</Label>
              <Select value={locationValue} onValueChange={(value) => onLocationChange(value === 'all' ? null : value)}>
                <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todas</SelectItem>{locations.map((location) => <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Colaborador</Label>
              <Select value={employeeValue} onValueChange={(value) => onEmployeeChange(value === 'all' ? null : value)}>
                <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todos</SelectItem>{employees.map((employee) => <SelectItem key={employee.id} value={employee.id}>{employee.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Estado</Label>
              <Select value={statusValue} onValueChange={(value) => onStatusChange(value === 'all' ? null : value)}>
                <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todos</SelectItem>{statusOptions.map((status) => <SelectItem key={status} value={status}>{statusLabels[status] || status}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Conta / pagamento</Label>
              <Select value={paymentAccountValue} onValueChange={(value) => onPaymentAccountChange(value === 'all' ? null : value)}>
                <SelectTrigger className="mt-1.5 h-9 rounded-lg border-slate-200 bg-white text-sm"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todas</SelectItem>{paymentAccounts.map((account) => <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {actionsSlot}
            <Button type="button" variant="outline" className="h-9 rounded-lg border-slate-200 bg-white px-3 text-sm" onClick={onResetFilters} data-testid="sales-reset-filters-button-mobile">
              <RotateCcw className="mr-2 h-4 w-4" />
              Limpar
            </Button>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);